import { ArrowRight, Boxes, QrCode, FileText, Bell, Wrench, Camera, ClipboardList, ShieldCheck, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { GoldButton } from "../components/GoldButton";
import AppScreensGallery from "../components/AppScreensGallery";
import { IMAGES, AI_ASSISTANTS } from "../data/content";

const APP_FEATURES = [
  { icon: Boxes, t: "Gestion multi-profils", d: "Propriétaires, locataires, Airbnb et techniciens réunis dans un seul outil." },
  { icon: Wrench, t: "Interventions", d: "Planification, suivi et exécution des interventions techniques." },
  { icon: Camera, t: "Photos & documents", d: "Chaque photo et document est relié au bon logement et au bon dossier." },
  { icon: QrCode, t: "QR codes logement", d: "Un QR code permanent par logement, même si le contenu évolue." },
  { icon: ClipboardList, t: "Carnet d'entretien", d: "Carnet de Santé, entretien et états des lieux centralisés." },
  { icon: Bell, t: "Notifications & IA", d: "Alertes intelligentes et supervision par les IA de l'écosystème." },
];

const AI_ICONS = { gael: ShieldCheck, jetheque: Wrench, orion: Sparkles, jarvis: Boxes };

export default function Application() {
  return (
    <div data-testid="application-page">
      <Seo title="L'application" path="/application" description="L'application Speed Détresse : gestion des logements, Carnet de Santé, interventions, QR codes et assistants IA." />
      <PageHero
        overline="Le cœur opérationnel"
        title={["L'application", "Speed Détresse"]}
        subtitle="Le site collecte, l'application exécute. Elle traite, organise, suit et réalise chaque dossier créé depuis cette vitrine — sans double saisie."
        image={IMAGES.aiNodes}
        testid="application-hero"
      />

      <section className="relative z-10 mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {APP_FEATURES.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-white/10 bg-navy-surface/60 p-8 transition-colors hover:border-gold/40">
                <f.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif text-2xl text-white">{f.t}</h3>
                <p className="mt-3 text-slate-400">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Real app screens */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pt-4 pb-10 lg:px-8">
        <Reveal><Overline>L'application en images</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl tracking-tight text-white md:text-5xl">
            Découvrez les vrais écrans.
          </h2>
        </Reveal>
        <div className="mt-12">
          <AppScreensGallery testid="application-screens-gallery" />
        </div>
      </section>

      {/* API bridge */}
      <section className="relative z-10 border-y border-white/10 bg-navy-surface/40 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Overline>Passerelle sécurisée</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-4xl tracking-tight text-white md:text-5xl">
              Chaque demande du site devient un dossier
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300">
              Une demande n'est jamais un simple e-mail : elle alimente automatiquement l'application. Chaque dossier
              possède un identifiant, un statut, une date, une origine et un historique.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["Souscription", "Prospect / dossier"],
              ["Rendez-vous", "Demande de rendez-vous"],
              ["État des lieux", "Prestation à programmer"],
              ["Candidature", "Dossier recrutement"],
              ["Apporteur d'affaires", "Dossier partenaire"],
              ["Contact", "Ticket ou demande"],
            ].map(([from, to], i) => (
              <Reveal key={from} delay={i * 0.05}>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-navy p-5">
                  <span className="text-sm text-slate-300">{from}</span>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-gold" />
                  <span className="text-sm font-medium text-white">{to}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI detail */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal><Overline>L'innovation Speed Détresse — Gaël, Jéthèque, Orion & Jarvis</Overline></Reveal>
        <Reveal delay={0.05}>
          <p className="mt-4 max-w-2xl text-slate-400">
            L'intelligence artificielle accompagne le savoir-faire : elle est une aide à la décision et à l'organisation. Les décisions importantes et la validation des tarifs restent sous contrôle humain.
          </p>
        </Reveal>
        <div className="mt-12 space-y-6">
          {AI_ASSISTANTS.map((a, i) => {
            const Icon = AI_ICONS[a.id];
            return (
              <Reveal key={a.id} delay={i * 0.08}>
                <div data-testid={`ai-card-${a.id}`} className="grid gap-6 rounded-2xl border border-white/10 bg-navy-surface/60 p-8 md:grid-cols-[auto_1fr] md:p-10">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 text-gold">
                    <Icon className="h-7 w-7" strokeWidth={1.4} />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <h3 className="font-serif text-3xl text-white">{a.name}</h3>
                      <span className="text-sm uppercase tracking-wide text-gold">{a.role}</span>
                    </div>
                    <p className="mt-3 text-slate-300">{a.tagline}</p>
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {a.points.map((p) => (
                        <li key={p} className="flex gap-2.5 text-sm text-slate-400">
                          <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-gold" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-14 flex flex-wrap gap-4">
          <GoldButton to="/souscription" size="lg">Demander un rendez-vous <ArrowRight className="h-4 w-4" /></GoldButton>
          <GoldButton to="/tarifs" size="lg" variant="outline">Estimer mon abonnement</GoldButton>
        </Reveal>
      </section>
    </div>
  );
}
